<template>
  <div class="qrb">
    <label v-if="label" class="cp-label">{{ label }}</label>
    <div class="xi-editor">
      <TextEditorBox class="branding-tabs" :options="options" :editor="editor" :image="true" />
      <editor-content class="xi-editor-content" :editor="editor" />
    </div>
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-2'
import TextEditorBox from '@/components/basics/TextEditorBox.vue'
import { createRichEditor } from '../../../../utils/editorFactory'

/**
 * Self-contained rich-text body editor for a single question, for ONE language at a
 * time. Owns its own tiptap instance (created on mount, destroyed on unmount) so the
 * parent doesn't have to juggle N×lang editor instances. v-model is the HTML string
 * for the active language; switching `lang` re-seeds the editor from `value`.
 */
export default {
  name: 'QuestionRichBody',
  components: { EditorContent, TextEditorBox },
  props: {
    options: { type: Object, required: true },
    value: { type: String, default: '' },       // HTML for the active language
    lang: { type: String, default: 'de' },       // active language (re-seed on change)
    label: { type: String, default: '' }
  },
  data() {
    return { editor: null, syncing: false };
  },
  watch: {
    // When the active language changes, reload the editor with that language's HTML.
    lang() {
      if (!this.editor) return;
      this.syncing = true;
      this.editor.commands.setContent(this.value || '', false);
      this.$nextTick(() => { this.syncing = false; });
    },
    // External value change for the current language (e.g. reset) — keep in sync
    // without clobbering what the user is actively typing.
    value(val) {
      if (!this.editor || this.syncing) return;
      if (val !== this.editor.getHTML()) {
        this.editor.commands.setContent(val || '', false);
      }
    }
  },
  mounted() {
    this.editor = createRichEditor(this.value || '', {
      onUpdate: ({ editor }) => {
        if (this.syncing) return;
        this.$emit('input', editor.getHTML());
      }
    });
  },
  beforeDestroy() {
    if (this.editor && this.editor.destroy) this.editor.destroy();
    this.editor = null;
  }
};
</script>

<style scoped>
.qrb { margin-top: 0.75rem; }
.cp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.25rem; }
</style>
<style lang="scss">
@use "../../../../assets/style/General/richEditorBox.scss";
</style>
